import React, { useRef } from 'react';
import { toast } from 'react-toastify';
import ShareModalShell from 'components/ShareModalShell/ShareModalShell';
import { ReceivingTradeItem } from '../ActiveReceivingPage';
import { AssetUrls } from 'config/assets';

// Inline SVG card-back placeholder — never depends on S3/CDN, can't 403
const CARD_BACK_DATA_URI = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='223' height='311' viewBox='0 0 223 311'%3E%3Crect width='223' height='311' rx='12' fill='%231a1a2e'/%3E%3Crect x='8' y='8' width='207' height='295' rx='8' fill='none' stroke='%236F51E4' stroke-width='2' opacity='0.4'/%3E%3Ctext x='111.5' y='165' text-anchor='middle' fill='%236F51E4' font-size='48' opacity='0.5'%3E%E2%9C%A6%3C/text%3E%3C/svg%3E";

const getCardImageUrl = (card: ReceivingTradeItem): string => {
  if (card.image) {
    const resolved = AssetUrls.cards.ensureCDNUrl(card.image);
    // If ensureCDNUrl returned a valid-looking URL, use it
    if (resolved && resolved.startsWith('http')) {
      console.debug('[TradeShareModal] image resolved:', { name: card.name, image: card.image, resolved });
      return resolved;
    }
    // ensureCDNUrl returned empty/malformed — fall through to scryfallId
    console.warn('[TradeShareModal] ensureCDNUrl returned invalid URL, falling through:', { name: card.name, image: card.image, resolved });
  }
  if (card.scryfallId) {
    const cdnUrl = AssetUrls.cards.getCardImage(card.scryfallId, 'normal');
    console.debug('[TradeShareModal] using scryfallId CDN URL:', { name: card.name, scryfallId: card.scryfallId, cdnUrl });
    return cdnUrl;
  }
  console.warn('[TradeShareModal] no image or scryfallId, using card back:', { name: card.name });
  return AssetUrls.cards.defaultBack;
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  shareImageUrl: string | null;
  isLoadingImage: boolean;
  receivedCards: ReceivingTradeItem[];
}

const TradeShareModal: React.FC<Props> = ({
  isOpen,
  onClose,
  shareImageUrl,
  isLoadingImage,
  receivedCards,
}) => {
  // Track fallback step per card index — persists across React re-renders
  const fallbackStepRef = useRef<Map<number, number>>(new Map());

  // Chrome (backdrop, Esc, surface, gradient region) lives in ShareModalShell
  if (!isOpen) return null;

  const handleShare = async () => {
    if (isLoadingImage || !shareImageUrl) {
      toast.info('Share image is still generating...');
      return;
    }

    try {
      const response = await fetch(shareImageUrl);
      const blob = await response.blob();

      if (navigator.share && /Mobi|Android/i.test(navigator.userAgent)) {
        const file = new File([blob], 'my-trade.png', { type: 'image/png' });
        await navigator.share({
          files: [file],
          text: 'Check out my latest trade on TradeMagic! 🎴 trademagic.gg',
        });
      } else {
        await navigator.clipboard.write([
          new ClipboardItem({ 'image/png': blob }),
        ]);
        toast.success('Copied to clipboard — ready to paste!');
      }
    } catch (err: any) {
      if (err?.name !== 'AbortError') {
        toast.error('Failed to share image');
      }
    }
  };

  const displayCards = receivedCards.slice(0, 3);
  const cardRotations = displayCards.length === 1
    ? [0]
    : displayCards.length === 2
      ? [-6, 6]
      : [-12, 0, 12];
  const cardTranslations = displayCards.length === 1
    ? [0]
    : displayCards.length === 2
      ? [-55, 55]
      : [-100, 0, 100];

  const cardFan = (
    <div className="trade-share-card-fan">
      {displayCards.map((card, i) => (
        <div
          key={i}
          className="trade-share-card"
          style={{
            transform: `rotate(${cardRotations[i]}deg) translateX(${cardTranslations[i]}px)`,
            zIndex: i === 1 ? 2 : 1,
          }}
        >
          <img
            src={getCardImageUrl(card)}
            alt={card.name}
            onError={(e) => {
              const step = fallbackStepRef.current.get(i) || 0;
              fallbackStepRef.current.set(i, step + 1);
              const initialUrl = getCardImageUrl(card);
              const fallbacks = card.scryfallId
                ? [
                    AssetUrls.cards.getCardImage(card.scryfallId, 'normal'),
                    AssetUrls.cards.getCardImage(card.scryfallId, 'small'),
                    AssetUrls.cards.getCardImage(card.scryfallId, 'large'),
                  ].filter(url => url !== initialUrl)
                : [];
              console.warn('[TradeShareModal] fallback debug:', {
                name: card.name,
                src: e.currentTarget.src,
                scryfallId: card.scryfallId,
                initialUrl,
                fallbacks,
                step,
              });

              if (card.scryfallId) {

                if (step < fallbacks.length) {
                  e.currentTarget.src = fallbacks[step];
                  return;
                }
              }

              // All CDN sizes exhausted or no scryfallId — inline SVG placeholder
              e.currentTarget.src = CARD_BACK_DATA_URI;
            }}
          />
        </div>
      ))}
    </div>
  );

  const shareButtonLabel = isLoadingImage && !shareImageUrl ? 'Generating...' : 'Share';

  return (
    <ShareModalShell
      isOpen={isOpen}
      onClose={onClose}
      imageRegion={cardFan}
      title="Trade Complete 🎴"
      subtitle="Congratulations on your trade! You can copy the trade image to your clipboard and share it anywhere."
      actions={
        <>
          <button className="trade-share-btn-continue" onClick={onClose}>
            Continue
          </button>
          <button className="trade-share-btn-share" onClick={handleShare} disabled={isLoadingImage && !shareImageUrl}>
            {shareButtonLabel}
          </button>
        </>
      }
    />
  );
};

export default TradeShareModal;
